import { useState, useRef, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';
import { PasswordInput } from '@/components/ui/password-input';
import {
  User,
  Settings as SettingsIcon,
  Bell,
  Sliders,
  Shield,
  Lock,
  Edit2,
  Camera,
  X,
  Monitor,
  Download,
  FileText,
  ChevronRight,
} from 'lucide-react';

type SectionId = 'profile' | 'account' | 'notifications' | 'preferences' | 'security' | 'privacy';

export function Settings() {
  const [activeSection, setActiveSection] = useState<SectionId>('profile');

  const sections = [
    { id: 'profile' as SectionId, label: 'Profile', icon: User },
    { id: 'account' as SectionId, label: 'Account', icon: SettingsIcon },
    { id: 'notifications' as SectionId, label: 'Notifications', icon: Bell },
    { id: 'preferences' as SectionId, label: 'Preferences', icon: Sliders },
    { id: 'security' as SectionId, label: 'Security', icon: Shield },
    { id: 'privacy' as SectionId, label: 'Privacy & Data', icon: Lock },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-6 py-8">
        <h1 className="text-4xl font-bold text-gray-900 mb-8">Settings</h1>
        
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Sidebar Navigation */}
          <div className="lg:col-span-1">
            <div className="bg-white border border-gray-200 rounded-xl p-2 sticky top-6">
              <nav className="space-y-1">
                {sections.map((section) => (
                  <button
                    key={section.id}
                    onClick={() => setActiveSection(section.id)}
                    className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors text-left ${
                      activeSection === section.id
                        ? 'bg-[#024223] text-white'
                        : 'text-gray-700 hover:bg-gray-100'
                    }`}
                  >
                    <section.icon size={20} />
                    <span className="font-medium">{section.label}</span>
                  </button>
                ))}
              </nav>
            </div>
          </div>
          
          {/* Main Content */}
          <div className="lg:col-span-3">
            {activeSection === 'profile' && <ProfileSection />}
            {activeSection === 'account' && <AccountSection />}
            {activeSection === 'notifications' && <NotificationsSection />}
            {activeSection === 'preferences' && <PreferencesSection />}
            {activeSection === 'security' && <SecuritySection />}
            {activeSection === 'privacy' && <PrivacySection />}
          </div>
        </div>
      </div>
    </div>
  );
}

function ProfileSection() {
  const { user, refreshUser } = useAuth();
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isUploadingAvatar, setIsUploadingAvatar] = useState(false);
  const [avatarUrl, setAvatarUrl] = useState(user?.avatar_url || '');
  const avatarInputRef = useRef<HTMLInputElement>(null);
  const [formData, setFormData] = useState({
    name: user?.name || '',
    email: user?.email || '',
    phone: '',
    title: '',
    specialty: '',
    registrationNumber: '',
    bio: '',
  });

  // Load existing profile data from database
  useEffect(() => {
    async function loadProfile() {
      const authId = user?.supabaseAuthId || user?.id;
      if (!authId) { setIsLoading(false); return; }

      const { data: profile } = await supabase
        .from('profiles')
        .select('display_name, email, phone, job_title, location, specialty, registration_number, bio')
        .eq('auth_user_id', authId)
        .single();

      if (profile) {
        setFormData({
          name: profile.display_name || user?.name || '',
          email: profile.email || user?.email || '',
          phone: profile.phone || '',
          title: profile.job_title || '',
          specialty: profile.specialty || '',
          registrationNumber: profile.registration_number || '',
          bio: profile.bio || '',
        });
      }
      setIsLoading(false);
    }
    loadProfile();
  }, [user]);

  const handleAvatarUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be under 5MB');
      return;
    }

    setIsUploadingAvatar(true);
    try {
      const fileExt = file.name.split('.').pop()?.toLowerCase() || 'jpg';
      const fileName = `avatars/${Date.now()}-${Math.random().toString(36).slice(2, 8)}.${fileExt}`;

      const { error: uploadError } = await supabase.storage
        .from('tenant-assets')
        .upload(fileName, file, { upsert: true });
      if (uploadError) throw uploadError;

      const { data: urlData } = supabase.storage
        .from('tenant-assets')
        .getPublicUrl(fileName);

      const publicUrl = urlData.publicUrl;
      setAvatarUrl(publicUrl);

      // Save to profile immediately
      const authId = user?.supabaseAuthId || user?.id;
      if (authId) {
        await supabase
          .from('profiles')
          .update({ avatar_url: publicUrl })
          .eq('auth_user_id', authId);
        await refreshUser();
      }
      toast.success('Profile photo updated');
    } catch (error) {
      console.error('Avatar upload failed:', error);
      toast.error('Failed to upload photo');
    } finally {
      setIsUploadingAvatar(false);
      if (avatarInputRef.current) avatarInputRef.current.value = '';
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      toast.error('Full name is required');
      return;
    }

    if (!user?.supabaseAuthId && !user?.id) {
      toast.error('Unable to identify user account. Please try logging in again.');
      return;
    }

    setIsSaving(true);
    try {
      const updatePayload: Record<string, string> = {
        display_name: formData.name.trim(),
        phone: formData.phone.trim(),
        job_title: formData.title.trim(),
        specialty: formData.specialty.trim(),
        registration_number: formData.registrationNumber.trim(),
        bio: formData.bio.trim(),
      };

      // Determine the correct identifier column and value
      const authId = user.supabaseAuthId || user.id;

      const { error } = await supabase
        .from('profiles')
        .update(updatePayload)
        .eq('auth_user_id', authId);

      if (error) throw error;

      toast.success('Profile updated successfully');
      setIsEditing(false);
      await refreshUser();
    } catch (err) {
      console.error('Failed to update profile:', err);
      toast.error('Failed to save profile. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const getRoleBadge = () => {
    if (!user || !user.roles.length) return { text: 'User', className: 'bg-gray-100 text-gray-700' };
    
    const badges: Record<string, { text: string; className: string }> = {
      admin: { text: 'Tenant Admin', className: 'bg-green-100 text-green-700' },
      company_admin: { text: 'Branch Admin', className: 'bg-green-100 text-green-700' },
      sales_rep: { text: 'Sales Representative', className: 'bg-blue-100 text-blue-700' },
      healthcare_professional: { text: 'Healthcare Professional', className: 'bg-gray-100 text-gray-700' },
      user: { text: 'User', className: 'bg-gray-100 text-gray-700' },
    };
    
    // Show the first recognized role
    const firstRole = user.roles.find(r => badges[r]);
    return firstRole ? badges[firstRole] : { text: user.roles[0], className: 'bg-gray-100 text-gray-700' };
  };

  const roleBadge = getRoleBadge();

  return (
    <div className="space-y-6">
      <div className="bg-white border border-gray-200 rounded-xl p-6">
        <div className="flex items-start justify-between mb-6">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 mb-1">Profile Information</h2>
            <p className="text-gray-600">Update your personal information and professional details</p>
          </div>
          <button
            onClick={() => setIsEditing(!isEditing)}
            className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            {isEditing ? (
              <>
                <X size={16} />
                Cancel
              </>
            ) : (
              <>
                <Edit2 size={16} />
                Edit
              </>
            )}
          </button>
        </div>

        {/* Avatar Section */}
        <div className="flex items-center gap-6 mb-8 pb-8 border-b border-gray-200">
          <div className="relative">
            <div className="w-24 h-24 rounded-full bg-gradient-to-br from-[#E8F5F0] to-[#024223]/20 flex items-center justify-center overflow-hidden">
              {avatarUrl ? (
                <img src={avatarUrl} alt="Profile" className="w-full h-full object-cover" />
              ) : (
                <span className="text-3xl font-bold text-[#024223]">
                  {user?.name?.split(' ').map(n => n[0]).join('') || 'MS'}
                </span>
              )}
              {isUploadingAvatar && (
                <div className="absolute inset-0 bg-black/50 flex items-center justify-center rounded-full">
                  <Loader2 className="w-6 h-6 text-white animate-spin" />
                </div>
              )}
            </div>
            <button
              type="button"
              onClick={() => avatarInputRef.current?.click()}
              disabled={isUploadingAvatar}
              className="absolute bottom-0 right-0 w-8 h-8 bg-[#024223] text-white rounded-full flex items-center justify-center hover:bg-[#035A2F] transition-colors"
            >
              <Camera size={16} />
            </button>
            <input
              ref={avatarInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              onChange={handleAvatarUpload}
              className="hidden"
            />
          </div>
          <div>
            <h3 className="text-xl font-bold text-gray-900">{user?.name}</h3>
            <p className="text-gray-600">{user?.email}</p>
            <span className={`inline-block mt-2 px-3 py-1 rounded-full text-sm font-medium ${roleBadge.className}`}>
              {roleBadge.text}
            </span>
          </div>
        </div>

        {/* Form Fields */}
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Full Name *
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                disabled={!isEditing}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#024223] focus:border-transparent disabled:bg-gray-50 disabled:text-gray-500 transition-colors"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Email Address *
              </label>
              <input
                type="email"
                value={formData.email}
                disabled
                className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-500"
              />
              <p className="text-xs text-gray-500 mt-1">Email cannot be changed</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Phone Number
              </label>
              <input
                type="tel"
                value={formData.phone}
                onChange={(e) => setFormData({...formData, phone: e.target.value})}
                disabled={!isEditing}
                placeholder="+61 4XX XXX XXX"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#024223] focus:border-transparent disabled:bg-gray-50 disabled:text-gray-500 transition-colors"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Professional Title
              </label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => setFormData({...formData, title: e.target.value})}
                disabled={!isEditing}
                placeholder="e.g., General Practitioner"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#024223] focus:border-transparent disabled:bg-gray-50 disabled:text-gray-500 transition-colors"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Specialty
              </label>
              <select
                value={formData.specialty}
                onChange={(e) => setFormData({...formData, specialty: e.target.value})}
                disabled={!isEditing}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#024223] focus:border-transparent disabled:bg-gray-50 disabled:text-gray-500 transition-colors"
              >
                <option value="">Select specialty</option>
                <option value="general">General Practice</option>
                <option value="cardiology">Cardiology</option>
                <option value="neurology">Neurology</option>
                <option value="pediatrics">Pediatrics</option>
                <option value="surgery">Surgery</option>
                <option value="emergency">Emergency Medicine</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Registration Number
              </label>
              <input
                type="text"
                value={formData.registrationNumber}
                onChange={(e) => setFormData({...formData, registrationNumber: e.target.value})}
                disabled={!isEditing}
                placeholder="AHPRA Registration"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#024223] focus:border-transparent disabled:bg-gray-50 disabled:text-gray-500 transition-colors"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Professional Bio
            </label>
            <textarea
              value={formData.bio}
              onChange={(e) => setFormData({...formData, bio: e.target.value})}
              disabled={!isEditing}
              rows={4}
              placeholder="Tell us about your experience and interests..."
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#024223] focus:border-transparent disabled:bg-gray-50 disabled:text-gray-500 transition-colors resize-none"
            />
          </div>

          {isEditing && (
            <div className="flex gap-3">
              <button
                type="submit"
                disabled={isSaving}
                className="px-6 py-2 bg-[#024223] text-white rounded-lg font-medium hover:bg-[#035A2F] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSaving ? 'Saving...' : 'Save Changes'}
              </button>
              <button
                type="button"
                onClick={() => setIsEditing(false)}
                disabled={isSaving}
                className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
            </div>
          )}
        </form>
      </div>
    </div>
  );
}

function AccountSection() {
  const { user } = useAuth();
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [passwordSuccess, setPasswordSuccess] = useState(false);
  const [isChangingPassword, setIsChangingPassword] = useState(false);

  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setPasswordError(null);
    setPasswordSuccess(false);

    // Validate current password is provided
    if (!passwordData.currentPassword) {
      setPasswordError('Current password is required');
      return;
    }

    // Validate new password meets minimum requirements
    if (passwordData.newPassword.length < 8) {
      setPasswordError('New password must be at least 8 characters long');
      return;
    }

    // Validate passwords match
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setPasswordError('New passwords do not match');
      return;
    }

    // Validate new password is different from current
    if (passwordData.currentPassword === passwordData.newPassword) {
      setPasswordError('New password must be different from current password');
      return;
    }

    setIsChangingPassword(true);
    try {
      // First, verify the current password by attempting to sign in
      const { error: signInError } = await supabase.auth.signInWithPassword({
        email: user?.email || '',
        password: passwordData.currentPassword,
      });

      if (signInError) {
        setPasswordError('Current password is incorrect');
        return;
      }

      // Update to the new password
      const { error: updateError } = await supabase.auth.updateUser({
        password: passwordData.newPassword,
      });

      if (updateError) throw updateError;

      setPasswordSuccess(true);
      setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
      toast.success('Password updated successfully');
    } catch (err) {
      console.error('Failed to change password:', err);
      const message = err instanceof Error ? err.message : 'Failed to update password. Please try again.';
      setPasswordError(message);
      toast.error('Failed to update password');
    } finally {
      setIsChangingPassword(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Company Information */}
      {user?.company_name && (
        <div className="bg-white border border-gray-200 rounded-xl p-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Company Information</h2>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium text-gray-600">Company Name</label>
              <div className="text-lg font-semibold text-gray-900 mt-1">{user.company_name}</div>
            </div>
            {user.company_id && (
              <div>
                <label className="text-sm font-medium text-gray-600">Company ID</label>
                <div className="text-sm text-gray-700 mt-1">{user.company_id}</div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Change Password */}
      <div className="bg-white border border-gray-200 rounded-xl p-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-4">Change Password</h2>

        {passwordError && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
            {passwordError}
          </div>
        )}

        {passwordSuccess && (
          <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg text-green-700 text-sm">
            Password updated successfully!
          </div>
        )}

        <form onSubmit={handlePasswordSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Current Password
            </label>
            <PasswordInput
              value={passwordData.currentPassword}
              onChange={(e) => setPasswordData({...passwordData, currentPassword: e.target.value})}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#024223] focus:border-transparent"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              New Password
            </label>
            <PasswordInput
              value={passwordData.newPassword}
              onChange={(e) => setPasswordData({...passwordData, newPassword: e.target.value})}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#024223] focus:border-transparent"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Confirm New Password
            </label>
            <PasswordInput
              value={passwordData.confirmPassword}
              onChange={(e) => setPasswordData({...passwordData, confirmPassword: e.target.value})}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#024223] focus:border-transparent"
            />
          </div>
          <button
            type="submit"
            disabled={isChangingPassword}
            className="px-6 py-2 bg-[#024223] text-white rounded-lg font-medium hover:bg-[#035A2F] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isChangingPassword ? 'Updating...' : 'Update Password'}
          </button>
        </form>
      </div>

      {/* Danger Zone */}
      <div className="bg-red-50 border border-red-200 rounded-xl p-6">
        <h2 className="text-2xl font-bold text-red-900 mb-2">Danger Zone</h2>
        <p className="text-red-700 mb-4">
          To delete your account, please contact your administrator.
        </p>
      </div>
    </div>
  );
}

function NotificationsSection() {
  const { user } = useAuth();
  const [isSaving, setIsSaving] = useState(false);
  const defaultSettings = {
    emailNotifications: true,
    simulationReminders: true,
    weeklyProgress: true,
    achievementAlerts: true,
    newSimulations: false,
    teamUpdates: true,
  };
  const [settings, setSettings] = useState(defaultSettings);

  // Load saved preferences from Supabase Auth user metadata
  useEffect(() => {
    async function loadPrefs() {
      const { data: { user: authUser } } = await supabase.auth.getUser();
      const saved = authUser?.user_metadata?.notification_preferences;
      if (saved && typeof saved === 'object') {
        setSettings({ ...defaultSettings, ...saved });
      }
    }
    loadPrefs();
  }, []);

  const notificationOptions = [
    { key: 'emailNotifications' as const, label: 'Email Notifications', description: 'Receive notifications via email' },
    { key: 'simulationReminders' as const, label: 'Simulation Reminders', description: 'Get reminded to complete pending simulations' },
    { key: 'weeklyProgress' as const, label: 'Weekly Progress Report', description: 'Receive weekly summary of your performance' },
    { key: 'achievementAlerts' as const, label: 'Achievement Alerts', description: 'Get notified when you earn badges' },
    { key: 'newSimulations' as const, label: 'New Simulations', description: 'Alert me when new simulations are available' },
    { key: 'teamUpdates' as const, label: 'Team Updates', description: 'Receive updates about team performance' },
  ];

  const handleSave = async () => {
    if (!user?.supabaseAuthId && !user?.id) {
      toast.error('Unable to identify user account');
      return;
    }

    setIsSaving(true);
    try {
      const authId = user.supabaseAuthId || user.id;

      const { error } = await supabase.auth.updateUser({
        data: { notification_preferences: settings },
      });

      if (error) throw error;

      // Also persist to profiles metadata if available
      await supabase
        .from('profiles')
        .update({ updated_at: new Date().toISOString() })
        .eq('auth_user_id', authId);

      toast.success('Notification preferences saved');
    } catch (err) {
      console.error('Failed to save notification preferences:', err);
      toast.error('Failed to save preferences. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="bg-white border border-gray-200 rounded-xl p-6">
      <h2 className="text-2xl font-bold text-gray-900 mb-4">Notification Preferences</h2>
      <p className="text-gray-600 mb-6">Choose what updates you want to receive</p>

      <div className="space-y-6">
        {notificationOptions.map((option) => (
          <div key={option.key} className="flex items-center justify-between py-4 border-b border-gray-200 last:border-0">
            <div>
              <div className="font-medium text-gray-900">{option.label}</div>
              <div className="text-sm text-gray-600">{option.description}</div>
            </div>
            <button
              onClick={() => setSettings({...settings, [option.key]: !settings[option.key]})}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                settings[option.key] ? 'bg-[#024223]' : 'bg-gray-300'
              }`}
              role="switch"
              aria-checked={settings[option.key]}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  settings[option.key] ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>
        ))}
      </div>

      <button
        onClick={handleSave}
        disabled={isSaving}
        className="mt-6 px-6 py-2 bg-[#024223] text-white rounded-lg font-medium hover:bg-[#035A2F] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {isSaving ? 'Saving...' : 'Save Preferences'}
      </button>
    </div>
  );
}

function PreferencesSection() {
  const { user } = useAuth();
  const [isSaving, setIsSaving] = useState(false);
  const defaultPrefs = {
    language: 'en',
    timezone: 'Australia/Sydney',
    dateFormat: 'DD/MM/YYYY',
  };
  const [prefs, setPrefs] = useState(defaultPrefs);

  // Load saved preferences from Supabase Auth user metadata
  useEffect(() => {
    async function loadPrefs() {
      const { data: { user: authUser } } = await supabase.auth.getUser();
      const saved = authUser?.user_metadata?.display_preferences;
      if (saved && typeof saved === 'object') {
        setPrefs({ ...defaultPrefs, ...saved });
      }
    }
    loadPrefs();
  }, []);

  const handleSave = async () => {
    if (!user?.supabaseAuthId && !user?.id) {
      toast.error('Unable to identify user account');
      return;
    }

    setIsSaving(true);
    try {
      const { error } = await supabase.auth.updateUser({
        data: { display_preferences: prefs },
      });

      if (error) throw error;

      toast.success('Display preferences saved');
    } catch (err) {
      console.error('Failed to save display preferences:', err);
      toast.error('Failed to save preferences. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="bg-white border border-gray-200 rounded-xl p-6">
      <h2 className="text-2xl font-bold text-gray-900 mb-6">Display Preferences</h2>

      <div className="space-y-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Language
          </label>
          <select
            value={prefs.language}
            onChange={(e) => setPrefs({...prefs, language: e.target.value})}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#024223] focus:border-transparent"
          >
            <option value="en">English</option>
            <option value="es">Spanish</option>
            <option value="fr">French</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Timezone
          </label>
          <select
            value={prefs.timezone}
            onChange={(e) => setPrefs({...prefs, timezone: e.target.value})}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#024223] focus:border-transparent"
          >
            <option value="Australia/Sydney">Sydney (GMT+11)</option>
            <option value="Australia/Melbourne">Melbourne (GMT+11)</option>
            <option value="Australia/Brisbane">Brisbane (GMT+10)</option>
            <option value="Australia/Perth">Perth (GMT+8)</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Date Format
          </label>
          <select
            value={prefs.dateFormat}
            onChange={(e) => setPrefs({...prefs, dateFormat: e.target.value})}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#024223] focus:border-transparent"
          >
            <option value="DD/MM/YYYY">DD/MM/YYYY</option>
            <option value="MM/DD/YYYY">MM/DD/YYYY</option>
            <option value="YYYY-MM-DD">YYYY-MM-DD</option>
          </select>
        </div>

        <button
          onClick={handleSave}
          disabled={isSaving}
          className="px-6 py-2 bg-[#024223] text-white rounded-lg font-medium hover:bg-[#035A2F] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isSaving ? 'Saving...' : 'Save Preferences'}
        </button>
      </div>
    </div>
  );
}

function SecuritySection() {
  return (
    <div className="space-y-6">
      {/* Current Session */}
      <div className="bg-white border border-gray-200 rounded-xl p-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-4">Current Session</h2>
        <div className="flex items-start gap-4 p-4 border border-gray-200 rounded-lg">
          <div className="w-12 h-12 rounded-lg bg-green-50 flex items-center justify-center">
            <Monitor size={24} className="text-green-600" />
          </div>
          <div>
            <div className="font-semibold text-gray-900">This Device</div>
            <div className="text-sm text-gray-600">You are currently logged in</div>
            <span className="inline-block mt-1 px-2 py-1 bg-green-100 text-green-700 rounded text-xs font-medium">
              Active
            </span>
          </div>
        </div>
      </div>

      {/* Two-Factor Authentication */}
      <div className="bg-white border border-gray-200 rounded-xl p-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Two-Factor Authentication</h2>
        <p className="text-gray-600 mb-4">Add an extra layer of security to your account</p>
        <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-amber-700 text-sm">
          Two-factor authentication will be available in an upcoming release.
        </div>
      </div>
    </div>
  );
}

function PrivacySection() {
  const privacyActions = [
    {
      icon: FileText,
      title: 'Privacy Policy',
      description: 'Read our privacy policy',
      href: 'https://medd.com.au/privacy-policy',
    },
    {
      icon: Shield,
      title: 'Data Security',
      description: 'Learn how we protect your data',
      href: 'https://medd.com.au/security',
    },
  ];

  return (
    <div className="bg-white border border-gray-200 rounded-xl p-6">
      <h2 className="text-2xl font-bold text-gray-900 mb-6">Privacy & Data</h2>

      <div className="space-y-6">
        <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <h3 className="font-semibold text-gray-900 mb-2">Your Data is Protected</h3>
          <p className="text-sm text-gray-700">
            All your training data is encrypted and stored securely. We never share your personal information with third parties.
          </p>
        </div>

        <div className="space-y-4">
          {privacyActions.map((action, idx) => (
            <a
              key={idx}
              href={action.href}
              target="_blank"
              rel="noopener noreferrer"
              className="w-full flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors text-left"
            >
              <div className="flex items-center gap-3">
                <action.icon size={20} className="text-gray-600" />
                <div>
                  <div className="font-medium text-gray-900">{action.title}</div>
                  <div className="text-sm text-gray-600">{action.description}</div>
                </div>
              </div>
              <ChevronRight size={20} className="text-gray-400" />
            </a>
          ))}
        </div>
      </div>
    </div>
  );
}
